import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useSelector } from "react-redux";

import LoadingSpinner from "components/LoadingSpinner";
import usePdfViewer from "./usePdfViewer";
import useDisplayPdf from "./useDisplayPdf";
import { setIsFetchingCoverPage } from "app/store/transmission/slice/transmissionSlice";

import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PdfViewer = ({
  reportId,
  hasAlertBanner,
  zoomLevel = 1,
  isPreviousReport = false,
  fillContainer = false,
}) => {
  const {
    dispatch,
    showNotes,
    openDrawer,
    pdfWidth,
    isPdfLoading,
    currentRef,
    containerRef,
    currentPageNumber,
    getTotalPages,
    handleLastPageLoadSuccess,
    onPDFDocumentLoadSuccess,
    fetchReportPdf,
    checkIsFetchingCoverPage,
  } = usePdfViewer({ reportId, isPreviousReport });

  const {
    reportAccountDetail,
    previousReportPdfUrl,
    transmissionReportPdfUrl,
  } = useSelector(({ transmission }) => transmission);

  const [pageHeights, setPageHeights] = useState({});
  const pageRefs = useRef({});
  const previousZoomRef = useRef(zoomLevel);

  const blobUrl = isPreviousReport
    ? previousReportPdfUrl
    : transmissionReportPdfUrl;
  const displayFile = useDisplayPdf(blobUrl);

  useEffect(() => {
    if (reportAccountDetail) {
      const fetchCoverPage = checkIsFetchingCoverPage();
      dispatch(setIsFetchingCoverPage(fetchCoverPage));

      if (typeof fetchCoverPage === "boolean") {
        fetchReportPdf(fetchCoverPage);
      }
    }
  }, [checkIsFetchingCoverPage, reportAccountDetail]);

  const handlePageLoadSuccess = (index) => {
    const pageDiv = pageRefs.current[index];
    if (pageDiv) {
      const height = pageDiv.offsetHeight;
      setPageHeights((prev) => ({ ...prev, [index]: height }));
    }
    const pdfPages = getTotalPages();
    index === pdfPages && handleLastPageLoadSuccess();
  };

  useEffect(() => {
    const hasZoomChanged = previousZoomRef.current !== zoomLevel;
    previousZoomRef.current = zoomLevel;

    if (!hasZoomChanged || isPdfLoading) {
      return;
    }

    const activePageNumber = currentPageNumber;
    const pageNumber = activePageNumber || 1;

    const scrollToCurrentPageTopLeft = () => {
      const container = containerRef.current;
      const pageElement = currentRef.current?.[pageNumber];

      if (!container || !pageElement) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const pageRect = pageElement.getBoundingClientRect();

      container.scrollTo({
        top: pageRect.top - containerRect.top + container.scrollTop,
        left:
          zoomLevel < 1
            ? 0
            : pageRect.left - containerRect.left + container.scrollLeft,
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToCurrentPageTopLeft);
    });
  }, [zoomLevel, isPdfLoading, currentPageNumber, containerRef, currentRef]);

  return (
    <div
      className="view-pdf__document"
      style={{
        overflowX: "auto",
        overflowY: isPdfLoading ? "hidden" : "auto",
        ...(fillContainer
          ? { flex: "1 1 auto", minHeight: 0 }
          : {
              height: showNotes
                ? `calc(100vh - ${hasAlertBanner ? "253px" : "178px"})`
                : "80vh",
              minWidth: showNotes
                ? openDrawer
                  ? `calc(72% - 300px)`
                  : "72%"
                : "100%",
            }),
      }}
    >
      {isPdfLoading || (blobUrl && !displayFile) ? (
        <div className="view-pdf--loading">
          <LoadingSpinner />
        </div>
      ) : blobUrl ? (
        <div ref={containerRef} className="pdf-container">
          <Document
            file={displayFile}
            onLoadSuccess={onPDFDocumentLoadSuccess}
            loading=""
            onLoadError={(error) => console.error("error", error)}
          >
            {Array.from(new Array(getTotalPages()), (_item, index) => {
              const pageHeight = pageHeights[index + 1] || 800; // default fallback
              const scaledMarginBottom = pageHeight * (zoomLevel - 1);
              return (
                <div
                  key={`pdf_page_${index + 1}`}
                  ref={(el) => {
                    if (el) {
                      pageRefs.current[index + 1] = el;
                      currentRef.current[index + 1] = el;
                    }
                  }}
                  page-number={index + 1}
                  className={clsx({ "view-pdf__page": !showNotes })}
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: zoomLevel < 1 ? "top center" : "top left",
                    marginBottom: `${scaledMarginBottom}px`,
                    marginRight:
                      zoomLevel < 1 ? 0 : `${(zoomLevel - 1) * 200}px`,
                    ...(zoomLevel < 1 && {
                      display: "flex",
                      justifyContent: "center",
                    }),
                  }}
                >
                  <div style={{ zoom: 1 / Math.max(1, zoomLevel) }}>
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      loading=""
                      width={showNotes && pdfWidth}
                      scale={Math.max(1, zoomLevel - 1)}
                      onLoadSuccess={() => handlePageLoadSuccess(index + 1)}
                    />
                  </div>
                </div>
              );
            })}
          </Document>
        </div>
      ) : (
        <div className="view-pdf--loading">Report not available.</div>
      )}
    </div>
  );
};

export default PdfViewer;
